import { Alert, List } from '@ksyos/design-system';
import { useMemo } from 'react';
import type { FundusScreeningManualImportApiResponse } from '@common/interfaces/api/fundusScreeningApi';
import { ExcelParserCustomValidationType, ExcelParserValidationErrorType } from '@common/interfaces/excelParser';
import { formatString } from '@common/utils/formatString';
import { getSelectedLanguage } from '@frontend/translate';
import { getValidationErrorTranslation } from '@frontend/utils/getValidationErrorTranslation';

export type FundusScreeningManualImportErrors = {
    excelParserErrors: FundusScreeningManualImportApiResponse['excelParser']['errors'];
};

interface FundusScreeningManualImportErrorMessagesProps {
    excelParserErrors: FundusScreeningManualImportErrors['excelParserErrors'];
}

export function FundusScreeningManualImportErrorMessages(
    props: Readonly<FundusScreeningManualImportErrorMessagesProps>
) {
    const translate = getSelectedLanguage();

    const { translatedErrorMessages, hasMoreThanOneError } = useMemo(() => {
        const nextTranslatedErrorMessages = translateExcelParserErrors(props.excelParserErrors);

        return {
            hasMoreThanOneError: nextTranslatedErrorMessages.length > 1,
            translatedErrorMessages: nextTranslatedErrorMessages,
        };
    }, [props.excelParserErrors]);

    return (
        <Alert appearance="warning">
            {hasMoreThanOneError ? (
                <List styleType="bullet">
                    {translatedErrorMessages.map((errorMessage, index) => (
                        <List.Item key={index}>{errorMessage}</List.Item>
                    ))}
                </List>
            ) : (
                translatedErrorMessages.at(0)
            )}
        </Alert>
    );

    function translateExcelParserErrors(
        errors: FundusScreeningManualImportErrorMessagesProps['excelParserErrors']
    ): string[] {
        const errorsMap = new Map();

        for (const error of errors) {
            let translatedErrorMessage = getValidationErrorTranslation(`excelParser.${error.type}`)!;

            switch (error.type) {
                case ExcelParserValidationErrorType.worksheetNotFound:
                    if (!Array.isArray(error.value)) {
                        return [formatString(translatedErrorMessage, [error.value?.toString() ?? ''])];
                    }
                    break;
                case ExcelParserValidationErrorType.columnsNotFound:
                    if (Array.isArray(error.value)) {
                        return [formatString(translatedErrorMessage, [error.value.join(', ')])];
                    }
                    break;
                case ExcelParserCustomValidationType.organizationWithAgbCodeNotFound:
                case ExcelParserCustomValidationType.multipleOrganizationsWithSameAgbCodeFound:
                case ExcelParserCustomValidationType.multipleGroupsWithSameNameFound:
                case ExcelParserCustomValidationType.vipLiveCareGroupExternalReferenceNumberNotFound:
                case ExcelParserCustomValidationType.groupWithNameNotFound:
                    if (!Array.isArray(error.value)) {
                        translatedErrorMessage = formatString(translatedErrorMessage, [error.value?.toString() ?? '']);
                    }
                    break;
            }

            if (errorsMap.has(translatedErrorMessage)) {
                const existingCellsAddresses = errorsMap.get(translatedErrorMessage);

                errorsMap.set(translatedErrorMessage, [...existingCellsAddresses, error.cellAddress]);
            } else {
                errorsMap.set(translatedErrorMessage, [error.cellAddress]);
            }
        }

        return Array.from(errorsMap).map(([message, cells]) => {
            if (cells[0] === null) {
                return message;
            }

            const cellOrCells =
                cells.length <= 1
                    ? translate.fundusScreening.manualImport.cell
                    : translate.fundusScreening.manualImport.cells;

            return `${message} ${translate.common.for} ${cellOrCells}: ${cells.join(', ')}`;
        });
    }
}
